import { defineComponent as _defineComponent } from 'vue'
import FiltersMenu from '../common/FiltersMenu.vue'
import TableFieldFilter from '../common/TableFieldFilter.vue'

import { useJobFilterStore } from '@/components/field-view-next/JobsFilterStore'
import { computed, onMounted } from 'vue'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { Dictionary, groupBy, orderBy, sortBy } from 'lodash'
import { useScheduleFilterStore } from './ScheduleFilterStore'
import { until } from '@vueuse/core'


export default /*#__PURE__*/_defineComponent({
  __name: 'ScheduleFiltersMenu',
  setup(__props) {

const scheduleViewStore = useScheduleFilterStore()
const store = useJobFilterStore()
const { data: filterOptions, isFetched: isFetchedFilterOptions } =
  useGetJobFilterOptions()

const divisionOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.divisionOptions,
    (d) => d.hubName
  )

  return withHeaders(grouped)
})

const resourceTypeOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.resourceTypeOptions,
    (d) => d.serviceName
  )

  return withHeaders(grouped)
})

const serviceProviderOptions = computed(() => {
  return [
    { id: null, name: 'No service provider' },
    ...(filterOptions?.value?.jobFilterOptions.serviceProviderOptions ?? []),
  ]
})

const withHeaders = function <T>(grouped: Dictionary<T[]>) {
  return orderBy(Object.keys(grouped))
    .map((groupKey) => [{ header: groupKey }, ...sortBy(grouped[groupKey])])
    .reduce((aggregate, items) => [...aggregate, ...items], [])
}

/**
 * Schedule view still relies on knowing every selected option id, and filtering locally.
 * We've recently updated filters on Map and jobs to work differently, where everything
 * is visible by default and filters are only used to narrow down results. Unfortunately
 * for now this means we need to detect if all filters are unselected, and use that to
 * "fake" everything being selected
 */

const setSelectedDivisions = ({ ids }: { ids: string[] }) => {
  store.$patch({
    selectedDivisions: ids,
  })
}

const setSelectedResourceTypes = ({ ids }: { ids: number[] }) => {
  store.$patch({
    selectedResourceTypes: ids,
  })
}

const setSelectedServiceProviders = ({ ids }: { ids: (string | null)[] }) => {
  store.$patch({
    selectedServiceProviders: ids,
  })
}

onMounted(async () => {
  await until(isFetchedFilterOptions).toBe(true)

  // handle division changes
  if (store.selectedDivisions.length === 0) {
    setSelectedDivisions({
      ids:
        filterOptions.value?.jobFilterOptions.divisionOptions.map(
          (d) => d.id
        ) || [],
    })
  }

  // handle resource type changes
  if (store.selectedResourceTypes.length === 0) {
    setSelectedResourceTypes({
      ids:
        filterOptions.value?.jobFilterOptions.resourceTypeOptions.map(
          (d) => d.id
        ) || [],
    })
  }

  // handle service provider changes
  if (store.selectedServiceProviders.length === 0) {
    setSelectedServiceProviders({
      ids: [
        null,
        ...(filterOptions.value?.jobFilterOptions.serviceProviderOptions.map(
          (d) => d.id
        ) || []),
      ],
    })
  }
})

const showOfflineUnits = computed({
  get() {
    return scheduleViewStore.showOfflineUnits
  },
  set(value) {
    scheduleViewStore.updateShowOfflineUnits(value!)
  },
})

const showStandbyUnits = computed({
  get() {
    return scheduleViewStore.showStandbyUnits
  },
  set(value) {
    scheduleViewStore.updateShowStandbyUnits(value!)
  },
})

const showUnitsWithNoAssignedJobs = computed({
  get() {
    return scheduleViewStore.showUnitsWithNoAssignedJobs
  },
  set(value) {
    scheduleViewStore.updateShowUnitsWithNoAssignedJobs(value!)
  },
})

return { __sfc: true,scheduleViewStore, store, filterOptions, isFetchedFilterOptions, divisionOptions, resourceTypeOptions, serviceProviderOptions, withHeaders, setSelectedDivisions, setSelectedResourceTypes, setSelectedServiceProviders, showOfflineUnits, showStandbyUnits, showUnitsWithNoAssignedJobs, FiltersMenu, TableFieldFilter }
}

})